import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Globe2, LogOut, Mail, Phone, Save, Truck, User } from 'lucide-react'
import { NovaPoshtaFields } from '@/components/NovaPoshtaFields'
import { UkrposhtaFields } from '@/components/UkrposhtaFields'
import type { ApiError } from '@/lib/api'
import { api } from '@/lib/api'
import { useT } from '@/lib/i18n'
import { haptic } from '@/lib/telegram'
import { useAppStore } from '@/lib/store'
import { useStoreTheme } from '@/lib/useStoreTheme'
import {
  StorefrontCard,
  StorefrontLoading,
  StorefrontPage,
} from '@/components/StorefrontPage'

interface Profile {
  id: number
  name: string
  email: string
  phone: string
  language: string
  saved_delivery_type: string
  saved_city: string
  saved_city_ref: string
  saved_warehouse: string
  saved_warehouse_ref: string
}

type AppLang = 'uk' | 'en' | 'ru'

const DEFAULT_LANGUAGES: AppLang[] = ['uk', 'en', 'ru']

function supportedProfileLanguages(settings?: { supported_languages?: string[] }): AppLang[] {
  const languages = (settings?.supported_languages ?? DEFAULT_LANGUAGES).filter(
    (language): language is AppLang => language === 'uk' || language === 'en' || language === 'ru',
  )
  return languages.length > 0 ? languages : DEFAULT_LANGUAGES
}

function profilePhonePlaceholder(settings?: { phone_placeholder?: string }): string | undefined {
  return settings?.phone_placeholder?.trim() || undefined
}

export function ProfileEditPage() {
  const lang = useAppStore((s) => s.lang)
  const T = useT('profileEdit')
  const common = useT('common')
  const qc = useQueryClient()
  const theme = useStoreTheme()

  const { data: profile, isLoading, isError } = useQuery<Profile>({
    queryKey: ['profile'],
    queryFn: () => api.get<Profile>('/me'),
  })

  const { data: settings } = useQuery<{ phone_placeholder?: string; supported_languages?: string[] }>({
    queryKey: ['settings'],
    queryFn: () => api.get('/settings'),
  })

  const setLang = useAppStore((s) => s.setLang)

  const [language, setLanguage] = useState<AppLang>('uk')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [deliveryType, setDeliveryType] = useState('')
  const [city, setCity] = useState('')
  const [cityRef, setCityRef] = useState('')
  const [warehouse, setWarehouse] = useState('')
  const [warehouseRef, setWarehouseRef] = useState('')
  const [savedMsg, setSavedMsg] = useState('')

  useEffect(() => {
    if (!profile) return
    const allowedLanguages = supportedProfileLanguages(settings)
    const profileLanguage = allowedLanguages.includes(profile.language as AppLang)
      ? (profile.language as AppLang)
      : allowedLanguages[0]
    setLanguage(profileLanguage) // eslint-disable-line react-hooks/set-state-in-effect
    setName(profile.name || '')
    setEmail(profile.email || '')
    setPhone(profile.phone || '')
    setDeliveryType(profile.saved_delivery_type || '')
    setCity(profile.saved_city || '')
    setCityRef(profile.saved_city_ref || '')
    setWarehouse(profile.saved_warehouse || '')
    setWarehouseRef(profile.saved_warehouse_ref || '')
  }, [profile, settings])

  useEffect(() => {
    if (deliveryType !== 'nova_poshta') {
      setCityRef('') // eslint-disable-line react-hooks/set-state-in-effect
      setWarehouseRef('')
    }
  }, [deliveryType])

  const saveMutation = useMutation<unknown, ApiError>({
    mutationFn: () =>
      api.patch('/me', {
        name: name.trim() || undefined,
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        language: language || undefined,
        saved_delivery_type: deliveryType,
        saved_city: city.trim(),
        saved_city_ref: cityRef || undefined,
        saved_warehouse: warehouse.trim(),
        saved_warehouse_ref: warehouseRef || undefined,
      }),
    onSuccess: () => {
      haptic('success')
      qc.invalidateQueries({ queryKey: ['profile'] })
      setLang(language)
      setSavedMsg(T.saved)
      setTimeout(() => setSavedMsg(''), 2500)
    },
    onError: () => haptic('error'),
  })

  const logoutMutation = useMutation<unknown, ApiError>({
    mutationFn: () => api.post('/auth/logout', {}),
    onSuccess: () => {
      window.dispatchEvent(new CustomEvent('auth:logout'))
    },
  })

  if (isLoading) {
    return (
      <StorefrontPage backTo="/profile" backLabel={T.back}>
        <StorefrontLoading message={common.loading} />
      </StorefrontPage>
    )
  }
  if (isError) {
    return (
      <StorefrontPage backTo="/profile" backLabel={T.back}>
        <p className="p-4 text-center text-xs font-semibold text-rose-500">{T.error}</p>
      </StorefrontPage>
    )
  }

  const phonePlaceholder = profilePhonePlaceholder(settings)

  const inputClass = `w-full rounded-xl border px-3.5 py-2.5 text-xs focus:outline-none transition ${
    theme.isDarkTheme
      ? 'border-[#3a4352] bg-[#1a202c] text-[#f8f3ea] placeholder-slate-500 focus:border-amber-400 focus:ring-2 focus:ring-amber-500/20'
      : 'border-slate-200 bg-slate-50/50 text-slate-900 focus:ring-2 focus:ring-brand-500 focus:bg-white'
  }`

  const labelClass = `mb-1.5 block text-xs font-medium ${
    theme.isDarkTheme ? 'text-slate-300' : 'text-slate-600'
  }`

  const iconClass = `h-3.5 w-3.5 ${
    theme.isDarkTheme ? 'text-amber-400' : 'text-brand-600'
  }`

  return (
    <StorefrontPage
      backTo="/profile"
      backLabel={T.back}
      title={T.title}
      className="pb-32"
    >
      <StorefrontCard className="mb-4 p-4 space-y-3">
        <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-400">
          <Globe2 className={iconClass} /> {T.language}
        </p>
        <div className="flex gap-2">
          {supportedProfileLanguages(settings).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setLanguage(option)}
              className={`flex-1 rounded-xl border py-2.5 text-xs font-bold transition shadow-xs ${
                language === option
                  ? theme.isDarkTheme
                    ? 'border-[#c9a24b] bg-[#c9a24b] text-[#17120f]'
                    : 'border-brand-600 bg-brand-600 text-white'
                  : theme.isDarkTheme
                    ? 'border-[#3a4352] bg-[#1a202c] text-slate-300 hover:border-slate-500'
                    : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
              }`}
            >
              {option === 'uk' ? T.languageUk : option === 'en' ? T.languageEn : 'Русский'}
            </button>
          ))}
        </div>
      </StorefrontCard>

      <StorefrontCard className="mb-4 p-4 space-y-4">
        <div>
          <label className="mb-1.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-400">
            <User className={iconClass} /> {T.name}
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={255}
            className={inputClass}
          />
        </div>
        <div>
          <label className="mb-1.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-400">
            <Mail className={iconClass} /> {T.email}
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            maxLength={255}
            className={inputClass}
          />
          <p className="mt-1 text-[11px] text-slate-400">{T.emailHint}</p>
        </div>
        <div>
          <label className="mb-1.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-400">
            <Phone className={iconClass} /> {T.phone}
          </label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            maxLength={255}
            placeholder={phonePlaceholder}
            className={inputClass}
          />
          <p className="mt-1 text-[11px] text-slate-400">{T.phoneHint}</p>
        </div>
      </StorefrontCard>

      <StorefrontCard className="mb-4 p-4 space-y-4">
        <div>
          <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-400">
            <Truck className={`h-4 w-4 ${theme.isDarkTheme ? 'text-amber-400' : 'text-brand-600'}`} /> {T.delivery}
          </p>
          <p className="mt-0.5 text-[11px] text-slate-400">{T.deliveryHint}</p>
        </div>
        <div>
          <label className={labelClass}>{T.deliveryType}</label>
          <select
            value={deliveryType}
            onChange={(e) => setDeliveryType(e.target.value)}
            className={inputClass}
          >
            {T.deliveryTypes.map((d) => (
              <option key={d.value} value={d.value}>{d.label}</option>
            ))}
          </select>
        </div>
        {deliveryType && (
          <div>
            {deliveryType === 'nova_poshta' ? (
              <div className="space-y-4">
                <NovaPoshtaFields
                  lang={lang}
                  city={city}
                  cityRef={cityRef}
                  warehouse={warehouse}
                  warehouseRef={warehouseRef}
                  onCityChange={(nextCity, nextRef) => {
                    const cityChanged = (city && nextCity !== city) || (cityRef && nextRef && nextRef !== cityRef)
                    setCity(nextCity)
                    setCityRef(nextRef)
                    if (cityChanged) {
                      setWarehouse('')
                      setWarehouseRef('')
                    }
                  }}
                  onWarehouseChange={(nextWarehouse, nextRef) => {
                    setWarehouse(nextWarehouse)
                    setWarehouseRef(nextRef)
                  }}
                />
              </div>
            ) : deliveryType === 'ukrposhta' ? (
              <div className="space-y-4">
                <UkrposhtaFields
                  lang={lang}
                  city={city}
                  warehouse={warehouse}
                  onCityChange={(nextCity) => {
                    setCity(nextCity)
                  }}
                  onWarehouseChange={(nextWarehouse) => {
                    setWarehouse(nextWarehouse)
                  }}
                />
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className={labelClass}>{T.city}</label>
                  <input
                    type="text"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder={T.cityPlaceholder}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>{T.warehouse}</label>
                  <input
                    type="text"
                    value={warehouse}
                    onChange={(e) => setWarehouse(e.target.value)}
                    placeholder={T.warehousePlaceholder}
                    className={inputClass}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </StorefrontCard>

      {saveMutation.error && (
        <p className="mb-3 px-1 text-xs font-semibold text-rose-500">{saveMutation.error.message}</p>
      )}
      {savedMsg && <p className="mb-3 px-1 text-xs font-semibold text-emerald-500">{savedMsg}</p>}

      <div
        className={`app-bottom-fixed app-bottom-panel flex gap-2 border-t p-3 shadow-lg backdrop-blur-md ${
          theme.isDarkTheme
            ? 'border-[#2d3748] bg-[#1a202c]/95'
            : 'border-slate-200 bg-white/95'
        }`}
      >
        <button
          onClick={() => { haptic('medium'); logoutMutation.mutate() }}
          disabled={logoutMutation.isPending}
          className={`flex items-center gap-1.5 rounded-xl border px-4 py-2.5 text-xs font-semibold transition shadow-xs ${
            theme.isDarkTheme
              ? 'border-[#3a4352] bg-[#242c38] hover:bg-[#2d3748] text-slate-300'
              : 'border-slate-200 bg-white hover:bg-slate-50 text-slate-700'
          }`}
        >
          <LogOut className="h-3.5 w-3.5" /> {T.logout}
        </button>
        <button
          onClick={() => { haptic('medium'); saveMutation.mutate() }}
          disabled={saveMutation.isPending}
          className={`flex flex-1 items-center justify-center gap-1.5 rounded-xl py-2.5 text-xs font-bold transition shadow-xs disabled:opacity-50 ${theme.accentBtnClass}`}
        >
          <Save className="h-3.5 w-3.5" />
          {saveMutation.isPending ? T.saving : T.save}
        </button>
      </div>
    </StorefrontPage>
  )
}
